<template>
  <div :class="{ 'connect-ex-account-view': !isOnboarding }">
    <template v-if="showSyncStatus && isOnboarding">
      <SyncStatus :loading="loading" :success="syncSuccess" :platform-logo="platformLogo" :total-trades="totalTrades" />
    </template>
    <template v-else>
      <div class="ca-container">
        <template>
          <img height="100" src="https://hub.swiset.com/wp-content/uploads/2024/03/Exness-New-Brand.png" alt="Exness.png">
        </template>
        <h2 class="mb-2">{{ $t('exnessTitle') }}</h2>
        <p class="mb-8">{{ $t('exnessSubtitle') }}</p>
        <v-form class="form-container" v-model="isValid" ref="form">
          <v-text-field class="mb-4" type="number" :label="$t('form_ConnectAccount.accountId.label')"
            :rules="touchedFields.accountId ? accountIdRules : []" v-model="mtAccountId"
            @input="handleInput('accountId')" @blur="touchField('accountId')" outlined />
          <v-select :label="$t('type_ConnectAccount')" :items="accountTypes" :rules="touchedFields.type ? accountTypeRule : []"
            v-model="typeChoosed" @change="handleInput('type')" @blur="touchField('type')" class="mb-4" outlined />
          <v-text-field class="mb-0" :label="$t('form_ConnectAccount.mtLogin.label')" :rules="touchedFields.login ? mtLoginRules : []"
            v-model="mtLogin" @input="handleInput('login')" @blur="touchField('login')" outlined />
          <div class="api-link-row mb-4">
            <a :href="paDomain" class="api-link-inline" target="_blank">{{ $t('whereis') }}</a>
          </div>
          <v-autocomplete class="mb-8"
            :items="typeChoosed == 'Demo' ? serversDemo.map(s => s.name) : serversLive.map(s => s.name)"
            value="https://nl-algo.excalls.io" :label="$t('form_ConnectAccount.mtInvestorPassword.label')"
            :rules="touchedFields.server ? mtInvestorPasswordRules : []" v-model="mtInvestorPassword"
            @input="handleInput('server')" @blur="touchField('server')" outlined />
          <v-progress-circular v-if="loading" indeterminate color="green"></v-progress-circular>
          <v-btn style="width: 100%;" :disabled="!isFormValid || loading" :loading="loading" @click="submitForm" v-if="!isOnboarding && !loading">
            {{ $t('submit') }}
          </v-btn>
        </v-form>
      </div>
    </template>
    <v-overlay :dark="false" v-model="modalSyncedV">
      <v-alert v-model="modalSyncedV" :title="modalTitle" closable close-label="Close Alert" :type="modalType">
        {{ modalText }}
        <v-btn class="closeButton" @click="() => closeAlert()">Ok</v-btn>
      </v-alert>
    </v-overlay>
    <!-- Move submit button to parent through portal -->
    <portal to="onboarding-actions" v-if="!showSyncStatus">
      <v-btn :disabled="!isFormValid || loading" :loading="loading" @click="submitForm">
        {{ $t('submit') }}
      </v-btn>
    </portal>
  </div>
</template>

<script>
import BrokerApiCredentialRepo from "../../repositories/brokerApiCredential";
import exnessServers from "./exnessServers";
import countries from "../Register/countries";
import { mapMutations } from 'vuex';
import authRepository from '../../repositories/auth';
import SyncStatus from '../../components/PlatformSync/SyncStatus.vue';

export default {
  name: "ConnectAccount",
  components: {
    SyncStatus
  },
  props: {
    isOnboarding: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      mt5Servers: [],
      mt4Servers: [],
      accountTypes: ['Demo', 'Live'],
      modalText: "",
      modalTitle: "",
      modalType: "",
      serversDemo: [],
      typeChoosed: "",
      serversLive: [],
      modalSyncedV: false,
      currentVersion: "Exness",
      server: "",
      versionDisabled: true,
      loading: false,
      paDomain: "https://my.exness.com/pa/settings/api-keys",
      mtLogin: "",
      mtInvestorPassword: "",
      mtAccountId: "",
      errorCode: 401,
      errorReason: "Unauthorized",
      isValid: false, // Changed from valid: false to isValid for clarity
      versionRules: [
        v => !!v || this.$t('validations_ConnectAccount.versionMandatory'),
      ],
      accountIdRules: [
        s => !!s || this.$t('form_ConnectAccount.accountId.mandatory'),
      ],
      mtLoginRules: [
        mtl => !!mtl || this.$t('form_ConnectAccount.mtLogin.mandatory'),
        mtl => !!mtl && mtl.startsWith('exnsk-') || this.$t('form_ConnectAccount.mtLogin.invalid'),
      ],
      mtInvestorPasswordRules: [
        mtl => !!mtl || this.$t('form_ConnectAccount.mtInvestorPassword.mandatory'),
      ],
      accountTypeRule: [
        a => !!a || this.$t('validations_ConnectAccount.type'),
      ],
      user: {},
      showSyncStatus: false,
      syncSuccess: false,
      totalTrades: 0,
      platformLogo: "https://hub.swiset.com/wp-content/uploads/2024/03/Exness-New-Brand.png",
      formTouched: false,  // Add this new property
      shouldValidate: false,
      touchedFields: {
        accountId: false,
        type: false,
        login: false,
        server: false
      }
    }
  },
  computed: {
    isFormValid() {
      return !!(
        this.mtAccountId &&
        this.typeChoosed &&
        this.mtLogin &&
        this.mtInvestorPassword
      );
    }
  },
  mounted: async function () {
    this.validateAuthenticated();
    this.user = (await authRepository.getUser()).data;
    this.user.details = (await authRepository.getUserDetails()).data;
    this.setUser(this.user);
    this.setUserDetails(this.user.details)
    this.init();
  },
  methods: {
    ...mapMutations(['setUser', 'setUserDetails']),
    validateAuthenticated: function () {
      if (this.$store.state.token === '')
        this.$router.push('/');
    },
    getExnessPALink: function (country) {
      BrokerApiCredentialRepo.getPADomain(country).then((response) => {
        if (response.status < 400) {
          if (response.data != "")
            this.paDomain = `https://${response.data}/pa/settings/api-keys`
        }
      });
    },
    init: function () {
      this.serversDemo = exnessServers.filter((server) => server.type === 'DEMO');
      this.serversLive = exnessServers.filter((server) => server.type === 'LIVE');
      let countryCode = countries.find((country) => country.name === this.$store.state.token ? this.$store.state.user.details.country : this.user.details.country).code;
      this.getExnessPALink(countryCode)
    },
    async submitForm() {
      this.shouldValidate = true;
      // Mark all fields as touched on submit
      Object.keys(this.touchedFields).forEach(field => {
        this.touchedFields[field] = true;
      });

      if (!this.$refs.form.validate()) {
        return;
      }
      // Rest of submit logic
      this.loading = true;
      this.showSyncStatus = true;

      let apiCredential = {
        apiKey: this.mtLogin,
        secret: this.mtLogin,
        apiCredentialType: "READ_ONLY",
        userId: this.$store.state.token ? this.$store.state.user.id : this.user.id,
        brokerName: "Exness",
        brokerAccountIdentifier: this.mtAccountId,
        apiUrl: exnessServers.find((server) => server.name == this.mtInvestorPassword).url,
        accountType: this.typeChoosed.toUpperCase()
      };
      this.loading = true;
      BrokerApiCredentialRepo.addApiCredential(apiCredential).then((response) => {
        if (response.status < 400) {
          if (this.isOnboarding) {
            this.$emit('sync-success', response.data);
            this.syncSuccess = true;
            this.totalTrades = response.data;
          } else {
            this.modalType = "success";
            this.modalText = this.$t('modal.success.text', { totalTrades: response.data });
            this.modalTitle = this.$i18n.messages[this.$i18n.locale]["modal"][this.modalType]["title"];
            this.modalSyncedV = true;
          }
        }
        this.loading = false;
      }).catch((error) => {
        this.loading = false;
        if (this.isOnboarding) {
          this.$emit('sync-error', error);
          this.syncSuccess = false;
        } else {
          this.modalType = "error";
          this.modalText = this.$t('modal.error.text');
          this.modalTitle = this.$i18n.messages[this.$i18n.locale]["modal"][this.modalType]["title"];
          this.modalSyncedV = true;
        }
      });
    },
    closeAlert: function () {
      this.modalSyncedV = false;
      if (this.modalType === 'success') {
        if (this.isOnboarding) {
          this.$router.push("/register/confirmation");
        } else if (this.$store.state.token) {
          this.$router.push("/profile");
        }
      }
    },
    handleInput(field) {
      this.touchField(field);
    },
    touchField(field) {
      this.touchedFields[field] = true;
    }
  },
  watch: {
    // Add watchers to trigger validation on any field change
    mtAccountId() { this.$refs.form.validate() },
    typeChoosed() { this.$refs.form.validate() },
    mtLogin() { this.$refs.form.validate() },
    mtInvestorPassword() { this.$refs.form.validate() }
  }
}
</script>



<style lang="scss">
.connect-ex-account-view {
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 40px;
}

.ca-container {
  width: 100%;
  max-width: 600px;

  img {
    margin-left: -35px;
  }

  .form-container {
    width: 100%;
    margin-top: 0;
  }

  .api-link {
    display: block;
    text-align: right;
    text-decoration: underline;
    color: inherit;
    margin-bottom: 16px;
  }

  .api-link-row {
    display: flex;
    justify-content: flex-end;
    margin-top: -8px;
  }

  .api-link-inline {
    color: var(--v-blue-base) !important;
    text-decoration: underline;
    font-size: 14px;
    white-space: nowrap;
  }
}

.v-btn.primary {
  background-color: var(--v-mainGreen-base) !important;
  color: var(--v-mainBlack-base) !important;
}

.v-application h2.text-h4,
.v-application p.subtitle-1,
.v-application .v-btn,
.v-application .v-label,
.v-application .v-text-field input {
  font-family: 'Avenir', sans-serif !important;
}

.closeButton {
  background: transparent !important;
  margin: auto;
  margin-top: 20px;
  color: #ffffff !important;

  &:hover {
    background: transparent !important;
  }
}
</style>